import { useId } from 'react'
import { cn } from '../../lib/cn'

interface FieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  /** Validation message. Present ⇒ the field renders in its error state. */
  error?: string
  /** Quiet hint under the control, e.g. "A sign-in link, no password." */
  help?: string
  /** Forwarded to the input — callers move focus here (React 19: ref is a prop). */
  ref?: React.Ref<HTMLInputElement>
}

/**
 * A club form field — letterspaced caps label over cream stock (components
 * card §II). Label colour is brass-DEEP, not brass: at 12px it is normal-size
 * text and plain brass fails AA on paper (see Eyebrow).
 *
 * The error message is wired through aria-describedby and announced via
 * role="alert", and the input is marked aria-invalid — the design card shows
 * the error in pin-flag red only, which colour-blind members would miss.
 */
export function Field({ label, error, help, className, ref, ...props }: FieldProps) {
  const uid = useId()
  const inputId = `field-${uid}`
  const errorId = `${inputId}-error`
  const helpId = `${inputId}-help`

  const describedBy = [error ? errorId : null, help ? helpId : null].filter(Boolean).join(' ')

  return (
    <div className={cn('mb-[18px]', className)}>
      <label
        htmlFor={inputId}
        className="mb-[7px] block text-xs font-semibold uppercase tracking-[0.16em] text-brass-deep"
      >
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(
          'w-full rounded-club border bg-parchment px-[15px] py-[13px] text-[15px] text-ink',
          'placeholder:text-ink-soft',
          // focus-visible outline comes from fireside.css; the border/glow here
          // is the design's own focus treatment on top of it.
          'focus:border-fairway focus:shadow-[0_0_0_3px_rgba(44,90,65,0.14)]',
          error ? 'border-flag' : 'border-ink/20',
        )}
        {...props}
      />
      {error && (
        <p id={errorId} role="alert" className="mt-[7px] text-[12.5px] text-flag">
          {error}
        </p>
      )}
      {help && (
        <p id={helpId} className="mt-[7px] text-[12.5px] text-ink-soft">
          {help}
        </p>
      )}
    </div>
  )
}
